/**
 * Text direction for user-written text.
 *
 * WHY NOT `dir="auto"` ALONE: the browser's rule is "the first strong character
 * wins". Persian and Arabic writers routinely open a sentence with a Latin term -
 * "API رو چک کن", "PR رو مرج کردم" - and `dir="auto"` lays those out
 * left-to-right, with the punctuation and the alignment on the wrong side. Here
 * the DOMINANT script decides instead, and the first strong character only
 * breaks a tie.
 */

/**
 * Right-to-left script blocks: Hebrew, Arabic (with Persian and Urdu
 * additions), Syriac, Thaana, NKo, Samaritan, Mandaic, and the Hebrew and
 * Arabic presentation forms.
 */
const RTL_SCRIPT = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFC]/u;

/** Any letter at all; whatever is not RTL among these counts as LTR. */
const LETTER = /\p{L}/u;

export type TextDirection = 'ltr' | 'rtl';

/**
 * `rtl` or `ltr` by majority of strong letters, or `undefined` when there are
 * none to go on (digits, emoji, punctuation) - callers then fall back to `auto`
 * and let the surrounding context decide.
 */
export function detectDirection(text: string): TextDirection | undefined {
  let rtl = 0;
  let ltr = 0;
  let first: TextDirection | undefined;

  for (const character of text) {
    // A letter first: the Arabic block also holds digits (۱۲۳) and punctuation,
    // which are weak and must not tip the balance.
    if (!LETTER.test(character)) {
      continue;
    }

    if (RTL_SCRIPT.test(character)) {
      rtl += 1;
      first ??= 'rtl';
    } else {
      ltr += 1;
      first ??= 'ltr';
    }
  }

  if (rtl === ltr) {
    return first;
  }

  return rtl > ltr ? 'rtl' : 'ltr';
}

/** A `dir` attribute value: the detected direction, or `auto` when there is none. */
export function dirFor(text: string): TextDirection | 'auto' {
  return detectDirection(text) ?? 'auto';
}
